/**
 *
 * CompanyDetails
 *
 */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Descriptions, PageHeader } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, RouteComponentProps } from 'react-router';

import * as S from './CompanyDetails.styles';
import {
  selectCompanyDetails,
  selectCompanyStatus,
} from '../../slice/selectors';
import { useCompanySlice } from '../../slice';
import { RouteConstants } from 'app/routes';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import CompanyStatusTag from 'app/components/CompanyStatusTag/CompanyStatusTag';
import CompanyInOtherEnvsTable from '../../parts/CompanyInOtherEnvsTable/CompanyInOtherEnvsTable';
import PermissionsWrapper from 'app/components/PermissionsWrapper/PermissionsWrapper';
import { PermissionsEnum } from 'app/containers/User/slice/types';
import { useAcceptSharedCompanyMutation } from 'app/api';
import {
  showAcceptedCompanyShareNotification,
  showDeclinedCompanyShareNotification,
} from 'utils/notifications';
import { handleError } from 'app/containers/Global/slice/helpers';
import { useRemoveSharedCompanyMutation } from '../../../../api';
import { selectCurrentUserId } from '../../../Authorization/slice/selectors';

interface IReactRouterParams {
  companyId: string;
}
interface IProps extends RouteComponentProps<IReactRouterParams> {}

const CompanyDetails = ({
  history,
  match: {
    params: { companyId },
  },
}: IProps) => {
  const { t } = useTranslation();
  const { actions } = useCompanySlice();
  const dispatch = useDispatch();

  const { loading, company } = useSelector(selectCompanyDetails);
  const selectedEnvId = useSelector(selectSelectedEnvId) as number;
  const userId = useSelector(selectCurrentUserId) as number;
  const companyStatus = useSelector(selectCompanyStatus);

  const [acceptSharedCompany] = useAcceptSharedCompanyMutation();
  const [removeSharedCompany] = useRemoveSharedCompanyMutation();

  useEffect(() => {
    if (companyId && selectedEnvId) {
      dispatch(
        actions.getCompanyById({
          envId: selectedEnvId,
          companyId: Number(companyId),
        }),
      );
    }
  }, [companyId, actions, dispatch, selectedEnvId]);

  const openCompanyForm = () => {
    dispatch(actions.setCompanyFormType({ type: 'edit', companyId }));
    dispatch(actions.toggleCompanyForm(true));
  };

  const respondToCompanyShare = async (isApproved: boolean) => {
    try {
      if (!company) {
        return;
      }

      const companyName = company.name;
      const companyId = company.id;
      const envId = selectedEnvId;

      if (isApproved) {
        await acceptSharedCompany({ envId, companyId }).unwrap();

        dispatch(actions.getCompanyById({ envId, companyId }));

        showAcceptedCompanyShareNotification(companyName);
      } else {
        await removeSharedCompany({
          envId,
          companyId,
          guestEnvId: envId,
        }).unwrap();

        history.push(
          generatePath(RouteConstants.company.list, {
            envId,
          }),
        );

        showDeclinedCompanyShareNotification(companyName);
      }

      dispatch(actions.getCompanies(envId));
    } catch (error) {
      handleError(error);
    }
  };

  const isOwnerEnv =
    selectedEnvId === company?.ownerEnvironment.hiikoEnvironment.id;

  const isOwnerUser =
    userId === company?.ownerEnvironment.hiikoEnvironment.user.id;

  if (!selectedEnvId) {
    return null;
  }

  return (
    <S.CompanyDetailsWrapper>
      {loading ? (
        <S.Spinner tip={t('Global.Loading', 'Loading...')} size="large" />
      ) : (
        <>
          <PageHeader
            onBack={() =>
              history.push(
                generatePath(RouteConstants.company.list, {
                  envId: selectedEnvId,
                }),
              )
            }
            className="page-header"
            title={company?.name}
            extra={[
              <PermissionsWrapper
                key="1"
                requiredEditPermissions={[
                  PermissionsEnum.companyAdministration,
                ]}
              >
                <Button type="primary" onClick={openCompanyForm}>
                  {t('Company.Details.Edit', 'Edit')}
                </Button>
              </PermissionsWrapper>,
            ]}
          />
          <S.DetailsWrapper>
            <Descriptions
              bordered
              column={2}
              title={t(
                'Company.Details.CompanyInformation',
                'Company information',
              )}
            >
              <Descriptions.Item
                label={t('Company.Details.Manager', 'Manager')}
              >
                {`${company?.user?.name || ''} ${
                  company?.user?.lastName || ''
                }`}
              </Descriptions.Item>
              <Descriptions.Item label={t('Company.Details.Status', 'Status')}>
                <CompanyStatusTag status={companyStatus} owner={isOwnerEnv} />
                {!isOwnerEnv && !companyStatus && (
                  <>
                    <Button
                      type="link"
                      onClick={() => {
                        respondToCompanyShare(true);
                      }}
                    >
                      {t('Company.Details.ApproveBtn', 'Approve')}
                    </Button>

                    <Button
                      type="link"
                      danger
                      onClick={() => {
                        respondToCompanyShare(false);
                      }}
                    >
                      {t('Company.Details.CancelBtn', 'Cancel')}
                    </Button>
                  </>
                )}
              </Descriptions.Item>

              <Descriptions.Item
                label={t('Company.Details.Company', 'Company')}
              >
                {`${company?.name}`}
              </Descriptions.Item>

              <Descriptions.Item label={t('Common.Environment', 'Environment')}>
                {`${company?.ownerEnvironment?.hiikoEnvironment?.name}`}
              </Descriptions.Item>

              <Descriptions.Item
                label={t('Company.Details.juridicalCode', 'Holding number')}
              >
                {`${company?.juridicalCode}`}
              </Descriptions.Item>

              <Descriptions.Item
                label={t('Company.Details.Address', 'Address')}
              >
                {`${company?.country}, ${company?.city}, ${company?.street} ${company?.houseNumber}`}
              </Descriptions.Item>

              <Descriptions.Item
                label={t('Company.Details.CompanyCode', 'Company code')}
              >
                {company?.companyCode}
              </Descriptions.Item>

              <Descriptions.Item
                label={t('Company.Details.VatCode', 'VAT code')}
              >
                {company?.vatCode}
              </Descriptions.Item>

              <Descriptions.Item
                label={t('Company.Details.farmerCode', "Farmer's number")}
              >
                {company?.farmerCode || '-'}
              </Descriptions.Item>

              <Descriptions.Item
                label={t('Company.Details.sprayingCode', 'Spray certificate')}
              >
                {company?.sprayingCode || '-'}
              </Descriptions.Item>
            </Descriptions>
            {isOwnerEnv && (
              <CompanyInOtherEnvsTable
                envsData={company?.companyHiikoEnvironments}
                selectedEnvId={selectedEnvId}
                isOwnerUser={isOwnerUser}
              />
            )}
          </S.DetailsWrapper>
        </>
      )}
    </S.CompanyDetailsWrapper>
  );
};

export default CompanyDetails;
